"use client";

import { cn } from "@jesus-central-church/shared";
import { useYouTubeLive } from "@jesus-central-church/youtube";
import { AnimatePresence, motion } from "motion/react";
import { X, YoutubeIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

export interface YouTubeLiveAlertProps {
  title?: string;
  description?: string;
  className?: string;
}

export function YouTubeLiveAlert({
  title = "We zijn nu LIVE!",
  description = "Kijk mee met onze livestream",
  className,
}: YouTubeLiveAlertProps) {
  const [isClosed, setIsClosed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { isLive, stream } = useYouTubeLive({
    channelId: process.env.NEXT_PUBLIC_YOUTUBE_CHANNEL_ID ?? "",
  });

  useEffect(() => {
    if (isLive) {
      setIsOpen(true);
    }
  }, [isLive]);

  const [isHovered, setIsHovered] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && !isClosed && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 30,
          }}
          className={cn(
            "fixed bottom-6 right-6 z-[100] max-w-[380px]",
            className,
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Glow effect behind card */}
          <motion.div
            className="absolute -inset-2 rounded-3xl bg-red-500/20 blur-xl"
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Main card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] shadow-2xl shadow-black/50 border border-white/10">
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-30">
              <motion.div
                className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-red-500/30"
                animate={{
                  scale: [1, 1.3, 1],
                  x: [0, 10, 0],
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-red-600/20"
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              />
            </div>

            {/* Close button */}
            <motion.button
              onClick={() => {
                setIsOpen(false);
                setIsClosed(true);
              }}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Sluiten"
            >
              <X className="w-4 h-4 text-white/70" />
            </motion.button>

            {/* Content */}
            <div className="relative p-5">
              {/* Live badge with pulse */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full">
                  {/* Pulsing dot */}
                  <span className="relative flex h-2.5 w-2.5">
                    <motion.span
                      className="absolute inline-flex h-full w-full rounded-full bg-white"
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [1, 0, 1],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                  </span>
                  <span className="text-xs font-bold text-white tracking-wider uppercase">
                    Live
                  </span>
                </div>

                {/* YouTube icon */}
                <YoutubeIcon className="w-5 h-5 text-white" />
                <Image
                  src="/logo-groen.png"
                  alt="YouTube"
                  width={20}
                  height={20}
                />
              </div>

              {/* Title */}
              <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wide mb-1">
                {title}
              </h3>

              {/* Description */}
              <p className="text-sm text-white/70 mb-5">{description}</p>

              {/* CTA Button */}
              <motion.a
                href={stream?.watchUrl ?? ""}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl",
                  "bg-gradient-to-r from-red-600 to-red-500",
                  "text-white font-semibold text-sm",
                  "shadow-lg shadow-red-500/30",
                  "transition-all duration-300",
                )}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 20px 40px -10px rgba(239, 68, 68, 0.4)",
                }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Play icon */}
                <motion.svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  animate={isHovered ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <path d="M8 5v14l11-7z" />
                </motion.svg>
                <span>Bekijk Livestream</span>
                <motion.svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  animate={isHovered ? { x: [0, 4, 0] } : {}}
                  transition={{
                    duration: 0.5,
                    repeat: isHovered ? Infinity : 0,
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </motion.svg>
              </motion.a>
            </div>

            {/* Animated bottom accent line */}
            <motion.div
              className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                backgroundSize: "200% 100%",
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
